// Visualizador do documento ORIGINAL (PDF multipágina, JPG, PNG, HEIC) — nunca converte o arquivo.
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft, ChevronRight, Download, ExternalLink, Loader2, RotateCw, ZoomIn, ZoomOut, FileWarning,
} from 'lucide-react';
import api from '@/lib/api';

export default function DocumentViewer({ documentId, filename, contentType, className = '' }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [page, setPage] = useState(1);
  const isPdf = /pdf/i.test(contentType || '') || /\.pdf$/i.test(filename || '');

  useEffect(() => {
    if (!documentId) return;
    let objUrl = null;
    setUrl(null); setError(null); setZoom(1); setRot(0); setPage(1);
    (async () => {
      try {
        const { data } = await api.get(`/smart-import/documents/${documentId}/file`, { responseType: 'blob' });
        objUrl = URL.createObjectURL(data);
        setUrl(objUrl);
      } catch (e) {
        setError(e?.response?.status === 404
          ? 'Arquivo original indisponível no storage.'
          : 'Não foi possível abrir o documento original.');
      }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [documentId]);

  const baixar = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'documento';
    a.click();
  };

  return (
    <div className={`rounded-xl border bg-slate-900/95 overflow-hidden flex flex-col ${className}`}
      data-testid="doc-viewer">
      <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-800 text-slate-200">
        <span className="text-[11px] font-semibold truncate flex-1" data-testid="doc-viewer-name">
          {filename || 'Documento original'}
        </span>
        {isPdf && (
          <>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-200 hover:text-white"
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="doc-viewer-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-[11px] tabular-nums" data-testid="doc-viewer-page">pág. {page}</span>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-200 hover:text-white"
              onClick={() => setPage((p) => p + 1)} data-testid="doc-viewer-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </>
        )}
        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-200 hover:text-white"
          onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.2).toFixed(2)))} data-testid="doc-viewer-zoom-out">
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-[11px] tabular-nums w-10 text-center" data-testid="doc-viewer-zoom">{Math.round(zoom * 100)}%</span>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-200 hover:text-white"
          onClick={() => setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)))} data-testid="doc-viewer-zoom-in">
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-200 hover:text-white"
          onClick={() => setRot((r) => (r + 90) % 360)} data-testid="doc-viewer-rotate">
          <RotateCw className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-200 hover:text-white"
          onClick={() => url && window.open(url, '_blank')} data-testid="doc-viewer-fullscreen">
          <ExternalLink className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-200 hover:text-white"
          onClick={baixar} data-testid="doc-viewer-download">
          <Download className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 min-h-[300px] max-h-[62vh] overflow-auto bg-slate-950/60 flex items-start justify-center">
        {error ? (
          <p className="m-auto text-xs text-amber-300 flex items-center gap-2 p-6" data-testid="doc-viewer-error">
            <FileWarning className="w-4 h-4" /> {error}
          </p>
        ) : !url ? (
          <p className="m-auto text-xs text-slate-300 flex items-center gap-2 p-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Abrindo o documento original…
          </p>
        ) : isPdf ? (
          <iframe key={`${page}-${zoom}`} title="documento" data-testid="doc-viewer-pdf"
            src={`${url}#page=${page}&zoom=${Math.round(zoom * 100)}`}
            className="w-full h-[62vh] bg-white" />
        ) : (
          <img src={url} alt={filename || 'documento'} data-testid="doc-viewer-image"
            className="transition-transform duration-150 max-w-full max-h-[62vh] m-auto object-contain"
            style={{ transform: `scale(${zoom}) rotate(${rot}deg)` }} />
        )}
      </div>
    </div>
  );
}
